import React from 'react'
import PropTypes from 'prop-types'
import Loader from 'react-loader-spinner'
import {FaIcon} from '../components'

const ICON_SIZES = {lg: '16px', md: '14px', sm: '12px'}

export const GGButton = ({
  label,
  onClick,
  icon,
  iconRight,
  variant = 'primary',
  size = 'lg',
  disabled = false,
  isLoading = false,
  usePro = false,
  ariaLabel,
  className,
}) => {
  const iconSize = ICON_SIZES[size] || ICON_SIZES.lg
  // The Pro variant renders a webfont <i>, which is sized by font-size rather
  // than by the box dimensions that size the free SVG icons
  const iconStyle = usePro ? {fontSize: iconSize} : {width: iconSize, height: iconSize}
  const isDisabled = disabled || isLoading
  const hasLeftIcon = !!icon || isLoading
  const iconOnly = !label && hasLeftIcon

  // Icon-only buttons use the left icon; a right icon without a label is
  // not a supported shape (it would render at text width with no text)
  if (process.env.NODE_ENV !== 'production' && !label && !hasLeftIcon && iconRight) {
    console.warn('GGButton: iconRight without a label is not supported - use `icon` for icon-only buttons')
  }
  // FaIcon renders aria-hidden, so an icon-only button has no accessible
  // name unless the caller provides one
  if (process.env.NODE_ENV !== 'production' && iconOnly && !ariaLabel) {
    console.warn('GGButton: icon-only button needs an `ariaLabel` for screen readers')
  }
  const classes = [
    'gg-link-button',
    variant,
    size,
    iconOnly && 'icon-only',
    !iconOnly && hasLeftIcon && 'with-icon',
    !iconOnly && iconRight && 'with-icon-right',
    className,
  ].filter(Boolean).join(' ')

  return (
    <button
      type="button"
      className={classes}
      disabled={isDisabled}
      aria-label={ariaLabel}
      onClick={isDisabled ? undefined : onClick}>

      {isLoading && (
        <span className="btn-icon-loader">
          <Loader type="Oval" color="currentColor" height={parseInt(iconSize, 10)} width={parseInt(iconSize, 10)}/>
        </span>
      )}
      {icon && !isLoading && (
        <span className="btn-icon">
          <FaIcon icon={icon} usePro={usePro} style={iconStyle}/>
        </span>
      )}
      {label && <span className="btn-label">{label}</span>}
      {iconRight && (
        <span className="btn-icon">
          <FaIcon icon={iconRight} usePro={usePro} style={iconStyle}/>
        </span>
      )}
    </button>
  )
}

GGButton.propTypes = {
  label: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string,
  // Only valid together with a label - icon-only buttons must use `icon`
  iconRight: PropTypes.string,
  variant: PropTypes.oneOf([ 'primary', 'secondary', 'ghost', 'link' ]),
  size: PropTypes.oneOf([ 'lg', 'md', 'sm' ]),
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  // Render icon/iconRight through the Font Awesome Pro webfont instead of the free SVG set
  usePro: PropTypes.bool,
  // Accessible name - required in practice for icon-only buttons
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
}
